import axios from "axios";
import { debug, formatSafeErrorMessage } from "../../../../../utils/debugLogger";
import { fetchCustomerById } from "../../../../../services/customerService";
import type { ConnectedProfile } from "../../../../../types/customer";
import type {
  BlogDraftInput,
  BlogPost,
  CreateBlogResponse,
  ListBlogsResponse,
  PublishBlogResponse,
  WordPressSiteOption,
} from "../types/wordpress.types";

const CONTENT_API_BASE = process.env.REACT_APP_CONTENT_API_URL || "";

function requireConfig(customerId: string, idToken: string): void {
  if (!customerId?.trim()) {
    throw new Error("Customer ID is required");
  }
  if (!idToken?.trim()) {
    throw new Error("Authentication token is required");
  }
  if (!CONTENT_API_BASE) {
    throw new Error(
      "REACT_APP_CONTENT_API_URL is not set. Add it to .env.local and restart."
    );
  }
}

function authHeaders(idToken: string) {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${idToken}`,
  };
}

/** Pull the most useful message out of an axios error from the content API. */
function toError(error: unknown, fallback: string): Error {
  debug.error(`${fallback}:`, formatSafeErrorMessage(error));
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { error?: string; message?: string; detail?: string }
      | undefined;
    const msg = data?.error || data?.message || data?.detail;
    if (msg) {
      const err = new Error(msg);
      // Another post already has this web address: the editor marks that field.
      if (error.response?.status === 409 && (data as { field?: string }).field === "slug") err.name = "SlugTakenError";
      return err;
    }
  }
  if (error instanceof Error) return error;
  return new Error(fallback);
}

/** POST /content/{customer_id}/blogs — create a blog draft. */
export const createBlogDraft = async (
  customerId: string,
  idToken: string,
  input: BlogDraftInput
): Promise<CreateBlogResponse> => {
  requireConfig(customerId, idToken);
  if (!input.title?.trim()) throw new Error("Title is required");
  if (!input.content?.trim()) throw new Error("Content is required");

  try {
    const { data } = await axios.post<CreateBlogResponse>(
      `${CONTENT_API_BASE}/${customerId}/blogs`,
      { platform: "blog", ...input },
      { headers: authHeaders(idToken) }
    );
    return data;
  } catch (error) {
    throw toError(error, "Failed to create blog draft");
  }
};

/** GET /content/{customer_id}/blogs — list all blog drafts (newest first). */
export const listBlogDrafts = async (
  customerId: string,
  idToken: string
): Promise<BlogPost[]> => {
  requireConfig(customerId, idToken);
  try {
    const { data } = await axios.get<ListBlogsResponse>(
      `${CONTENT_API_BASE}/${customerId}/blogs`,
      { headers: authHeaders(idToken) }
    );
    return data?.posts ?? [];
  } catch (error) {
    throw toError(error, "Failed to load blog drafts");
  }
};

/** GET /content/{customer_id}/blogs/{post_id} — fetch one blog draft. */
export const getBlogDraft = async (
  customerId: string,
  postId: string,
  idToken: string
): Promise<BlogPost> => {
  requireConfig(customerId, idToken);
  if (!postId?.trim()) throw new Error("post_id is required");
  try {
    const { data } = await axios.get<{ post: BlogPost }>(
      `${CONTENT_API_BASE}/${customerId}/blogs/${postId}`,
      { headers: authHeaders(idToken) }
    );
    return data.post;
  } catch (error) {
    throw toError(error, "Failed to load blog draft");
  }
};

/** PUT /content/{customer_id}/blogs/{post_id} — update fields on a blog draft. */
export const updateBlogDraft = async (
  customerId: string,
  postId: string,
  idToken: string,
  input: Partial<BlogDraftInput>
): Promise<{ contentVersion?: number }> => {
  requireConfig(customerId, idToken);
  if (!postId?.trim()) throw new Error("post_id is required");
  try {
    const { data } = await axios.put<{ contentVersion?: number }>(
      `${CONTENT_API_BASE}/${customerId}/blogs/${postId}`,
      input,
      { headers: authHeaders(idToken) }
    );
    return data || {};
  } catch (error) {
    if (axios.isAxiosError(error) && (error.response?.data as { code?: string } | undefined)?.code === "STALE_VERSION") {
      const stale = new Error((error.response?.data as { error?: string }).error || "This post was changed somewhere else.");
      stale.name = "StaleVersionError";
      throw stale;
    }
    throw toError(error, "Failed to update blog draft");
  }
};

/** DELETE /content/{customer_id}/blogs/{post_id} — delete a blog draft. */
export const deleteBlogDraft = async (
  customerId: string,
  postId: string,
  idToken: string
): Promise<void> => {
  requireConfig(customerId, idToken);
  if (!postId?.trim()) throw new Error("post_id is required");
  try {
    await axios.delete(`${CONTENT_API_BASE}/${customerId}/blogs/${postId}`, {
      headers: authHeaders(idToken),
    });
  } catch (error) {
    throw toError(error, "Failed to delete blog draft");
  }
};

/**
 * POST /content/{customer_id}/wordpress/blogs/{post_id}/publish?wordpress_site_id=...
 * Publishes a saved blog draft to the connected WordPress.com site.
 *
 * `contentHtml` is the premium "Journal" HTML rendered client-side (inline
 * styles, since WordPress.com strips <style>/classes). When provided, the
 * backend publishes it verbatim so the live post matches the in-app Preview.
 */
export const publishBlogToWordPress = async (
  customerId: string,
  postId: string,
  wordpressSiteId: string,
  idToken: string,
  contentHtml?: string
): Promise<PublishBlogResponse> => {
  requireConfig(customerId, idToken);
  if (!postId?.trim()) throw new Error("post_id is required");
  if (!wordpressSiteId?.trim()) {
    throw new Error("A WordPress site must be selected before publishing");
  }
  try {
    const { data } = await axios.post<PublishBlogResponse>(
      `${CONTENT_API_BASE}/${customerId}/wordpress/blogs/${postId}/publish`,
      contentHtml ? { content_html: contentHtml } : null,
      {
        headers: authHeaders(idToken),
        params: { wordpress_site_id: wordpressSiteId },
      }
    );
    return data;
  } catch (error) {
    throw toError(error, "Failed to publish to WordPress");
  }
};

/**
 * Extract connected WordPress.com sites from a customer's connected_profiles.
 * The site `id` doubles as the `wordpress_site_id` used when publishing.
 */
export const extractWordPressSites = (
  profiles?: ConnectedProfile[]
): WordPressSiteOption[] => {
  if (!Array.isArray(profiles)) return [];
  const sites: WordPressSiteOption[] = [];
  for (const profile of profiles) {
    if (!/wordpress/i.test(profile.platform || "")) continue;
    for (const detail of profile.details || []) {
      if (!detail?.id) continue;
      const metadata = (detail.metadata || {}) as Record<string, string>;
      sites.push({
        id: detail.id,
        name: metadata.name || detail.id,
        url: metadata.url || "",
      });
    }
  }
  return sites;
};

/**
 * Fetch the customer record and return its connected WordPress.com sites.
 * Used by the publish site selector and the connected-sites list.
 */
export const getConnectedWordPressSites = async (
  customerId: string,
  idToken: string
): Promise<WordPressSiteOption[]> => {
  requireConfig(customerId, idToken);
  try {
    const customer = await fetchCustomerById(customerId, idToken);
    return extractWordPressSites(customer.connected_profiles);
  } catch (error) {
    throw toError(error, "Failed to load connected WordPress sites");
  }
};

/**
 * DELETE /content/{customer_id}/wordpress/oauth/disconnect?wordpress_site_id=...
 * Removes the stored token for one connected WordPress.com site.
 */
export const disconnectWordPressSite = async (
  customerId: string,
  wordpressSiteId: string,
  idToken: string
): Promise<void> => {
  requireConfig(customerId, idToken);
  if (!wordpressSiteId?.trim()) throw new Error("wordpress_site_id is required");
  try {
    await axios.delete(
      `${CONTENT_API_BASE}/${customerId}/wordpress/oauth/disconnect`,
      {
        headers: authHeaders(idToken),
        params: { wordpress_site_id: wordpressSiteId },
      }
    );
  } catch (error) {
    throw toError(error, "Failed to disconnect WordPress site");
  }
};
